import React from 'react';
import I18n from 'i18n-js';
import track from 'react-tracking';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { LISTING_TYPE, MParticlePageName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { Listing } from '@reverbdotcom/commons/src/gql/graphql';
import ErrorDisplay from './discovery/error_display';
import { PageSettings } from './marketplace/map_params';
import NotFoundAnimation from './not_found_animation';
import TileCurationView from './discovery/curation/tile_curation_view';
import ListViewRowCard from './list_view_row_card';
import { RCListingGrid } from '@reverbdotcom/cadence/components/RCListingGrid/RCListingGrid';
import { times } from 'lodash';
import { PlaceholderListingRowCard } from '@reverbdotcom/commons/src/components/listing_row_card';
import { RCButton, RCListingCard, RCSiteWrapper, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { Filter } from '@reverbdotcom/commons/src/components/search_filters';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { TimesCircleAltIcon } from '@reverbdotcom/cadence/icons/react';
import RQLFacets from '@reverbdotcom/commons/src/components/rql_facets';
import experiments from '@reverbdotcom/commons/src/experiments';

interface IExternalProps {
  loading: boolean;
  listings: Listing[];
  trackingName?: string;
  emptyGrid?: JSX.Element;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  pageSettings?: PageSettings;
  isListView?: boolean;
  exposedSidebarOpen?: boolean;
  filters?: Filter[];
  clearFiltersDisabled?: boolean;
  toggleExposedSidebarOpen?: () => void;
  listingSearchTotal?: number;
  clearFilters?:()=>void;
  itemsToDisplay?: number,
  gridItems?: JSX.Element;
  showFlags?: boolean;
}

interface IListingCard {
  listing: Listing,
  idx: number,
  isAdmin: boolean,
  isListView: boolean,
  loading: boolean,
  trackingName?: string,
  trackingQuery?: string,
  trackingFilter?: string,
  trackingSort?: string,
  trackingPage?: number,
  pageSettings?: PageSettings,
  showingSold?: boolean,
  showFlag?: boolean;
}

interface ISidebarHeaderProps {
  closeSidebarFilters: () => void;
}

interface ISidebarFooterProps {
  clearFiltersDisabled: boolean;
  listingSearchTotal: number;
  clearFilters: () => void;
  closeSidebarFilters: () => void;
}

export type ListingProps = IExternalProps;

type ListingGridProps = ListingProps & { error?: boolean; showingSold?: boolean; };

export const BRAND_NEW_CONDITION_UUID = '7c3f45de-2ae0-4c81-8400-fdb6b1d74890';
const COMPONENT_NAME = 'GridView';
const ITEMS_TO_DISPLAY = 8;

const MarketplaceSidebarHeader = ({ closeSidebarFilters } : ISidebarHeaderProps) => (
  <div className="d-flex fx-justify-between fx-align-center">
    <I18N text="commons.marketplaceFilters.filterAndSort" className="weight-bold" />
    <button
      onClick={closeSidebarFilters}
      type="button"
      className="button-as-link color-primary weight-bold"
    >
      <RCTextWithIcon
        svgComponent={TimesCircleAltIcon}
        size="large"
      >
        {I18n.t('commons.dismissibleSidebar.close')}
      </RCTextWithIcon>
    </button>
  </div>
);

const MarketplaceSidebarFooter = ({
  clearFilters,
  clearFiltersDisabled,
  listingSearchTotal,
  closeSidebarFilters,
} : ISidebarFooterProps) =>{
  return (
    <div className="d-flex fx-justify-between fx-align-center">
      <button
        type="button"
        className="button-as-link color-primary weight-bold"
        onClick={clearFilters}
        disabled={!clearFiltersDisabled}
      >
        <I18N text="commons.marketplaceFilters.clearFilters" />
      </button>
      <RCButton variant="filled" onClick={closeSidebarFilters}>
        <I18N
          text="commons.marketplaceFilters.showResultsCount"
          args={{ count: I18n.toNumber(listingSearchTotal, { precision: 0 }) }}
        />
      </RCButton>
    </div>
  );
};

function GridEmptyState() {
  return (
    <div className="not-found">
      <h3 className="not-found__message">
        {I18n.t('discovery.noResults.nothingHere')}
      </h3>
      <NotFoundAnimation />
    </div>
  );
}

export function GridListingCard({
  listing,
  idx,
  isAdmin,
  isListView,
  loading,
  showingSold = false,
  trackingName = '',
  trackingQuery = '',
  trackingFilter = '',
  trackingSort = '',
  trackingPage = 0,
  pageSettings = null,
  showFlag = false,
} :IListingCard ) {
  if (isListView) {
    return (
      <ListViewRowCard
        key={listing.id}
        listing={listing}
        loading={loading}
        position={idx}
        trackingQuery={trackingQuery}
        showingSold={showingSold}
        showFlag={showFlag}
      />
    );
  }

  return (
    <ListingCard
      key={listing.id}
      listing={listing}
      position={idx}
      soldListing={showingSold}
      trackingName={trackingName}
      trackingQuery={trackingQuery}
      trackingFilter={trackingFilter}
      trackingSort={trackingSort}
      trackingPage={trackingPage}
      footerContent={isAdmin && (
        <TileCurationView
          listingId={listing.id}
          pageSettings={pageSettings}
          bumped={listing.bumped}
        />
      )}
      showFlag={showFlag}
    />
  );
}

export function ListingGrid({
  loading,
  listings,
  showingSold = false,
  error = false,
  isListView = false,
  filters = null,
  exposedSidebarOpen = false,
  toggleExposedSidebarOpen = undefined,
  listingSearchTotal = 0,
  clearFiltersDisabled = false,
  clearFilters = undefined,
  itemsToDisplay = ITEMS_TO_DISPLAY,
  emptyGrid = null,
  trackingName = '',
  trackingQuery = '',
  trackingFilter = '',
  trackingSort = '',
  trackingPage = 0,
  pageSettings = null,
  gridItems = null,
  showFlags = false,
}: ListingGridProps) {

  const inExposedFiltersExp = useExpEnabled(experiments.SRP_EXPOSED_FILTERS_V2_DESKTOP);
  const user = useUser();
  const listingGridItems = listings.map((listing, idx) => {
    return (
      <RCListingGrid.Item key={listing.id} colSpan={isListView ? 'all' : 'one'}>
        <GridListingCard
          listing={listing}
          idx={idx}
          isAdmin={user?.isAdmin}
          isListView={isListView}
          loading={loading}
          showingSold={showingSold}
          trackingName={trackingName}
          trackingQuery={trackingQuery}
          trackingFilter={trackingFilter}
          trackingSort={trackingSort}
          trackingPage={trackingPage}
          pageSettings={pageSettings}
          showFlag={showFlags}
        />
      </RCListingGrid.Item>
    );
  });

  if (error) {
    return <ErrorDisplay />;
  }

  function renderGridItems() {
    const noListings = listings.length === 0;
    const isLoadingWithNoListings = loading && noListings;

    if (isLoadingWithNoListings) {
      const itemsToDisplayInGrid = itemsToDisplay ?? ITEMS_TO_DISPLAY;
      return (
        <>
          {times(itemsToDisplayInGrid, index => (
            <RCListingGrid.Item key={index} colSpan={isListView ? 'all' : 'one'}>
              {isListView
                ? <PlaceholderListingRowCard />
                : <RCListingCard.Skeleton />
              }
            </RCListingGrid.Item>
          ))}
        </>
      );
    }

    if (noListings) {
      return <RCListingGrid.Item colSpan="all">
        {emptyGrid || <GridEmptyState />}
      </RCListingGrid.Item>;
    }

    return gridItems ?? listingGridItems;
  }

  function wrappedSidebar(children: React.ReactNode) {
    // This effectively exposes the filters sidebar next to the listings grid for this experiment on the Marketplace only
    if (inExposedFiltersExp && pageSettings.pageName === MParticlePageName.Marketplace && listings.length !== 0) return children;
    else {
      return (
        <div className="d-none tablet-d-block">
          {children}
        </div>
      );
    }
  }

  return (
    <RCSiteWrapper
      insideWrapper
      loading={loading}
      sidebarOpen={exposedSidebarOpen}
      sidebar={
        wrappedSidebar(
          <RCSiteWrapper.Sidebar sticky>
            <RCSiteWrapper.SidebarHeader>
              <MarketplaceSidebarHeader closeSidebarFilters={toggleExposedSidebarOpen} />
            </RCSiteWrapper.SidebarHeader>
            <RCSiteWrapper.SidebarContent>
              <RQLFacets
                filters={filters}
                loading={loading}
                trackingQuery={trackingQuery}
                inRCSiteWrapper
              />
            </RCSiteWrapper.SidebarContent>
            <RCSiteWrapper.SidebarFooter>
              <MarketplaceSidebarFooter
                clearFilters={clearFilters}
                clearFiltersDisabled={clearFiltersDisabled}
                closeSidebarFilters={toggleExposedSidebarOpen}
                listingSearchTotal={listingSearchTotal}
              />
            </RCSiteWrapper.SidebarFooter>
          </RCSiteWrapper.Sidebar>,
        )
      }
    >
      <RCListingGrid
        withSidebar={inExposedFiltersExp}
      >
        {renderGridItems()}
      </RCListingGrid>
    </RCSiteWrapper>
  );
}

export default track({
  componentName: COMPONENT_NAME,
  type: LISTING_TYPE,
})(ListingGrid);
